import { createSlice } from "@reduxjs/toolkit";
import {
  addUpdateCheckpoint,
  addEditNote,
  addReminder,
  fetchChecklistByConversationId,
  fetchMessagesByConversationId,
  fetchNotesByConversationId,
  fetchRemindersByConversationId,
  getChatsById,
  getChatsByTenant,
  updateConversation,
  fetchDocsByConversationId,
  createConversation,
  fetchCasesByConversationId,
  addEditCase,
  addEditSummary,
  fetchSummariesByConversationId,
  updateFileKey,
} from "./thunks";
import { message } from "antd";
import {
  handleAddUpdateCheckpointFulfilled,
  handleSendWebSocketMessage,
  upsertChatsByTenant,
} from "./helper";
import { showMessageNotification } from "../../utils/notificationService";
import { ROUTES } from "../../constant/routes";

const initialState = {
  isConnected: false,
  lastSocketMessage: null,
  chatsByTenants: {},
  loading: true,
  selectedChatId: null,
  newMessageCount: 0,
  reactions: null,
};

const chatsSlice = createSlice({
  name: "chats",
  initialState,
  reducers: {
    connectWebSocket: () => {},
    disconnectWebSocket: () => {},
    sendWebSocketMessage: handleSendWebSocketMessage,
    setKeyValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    removeChat: (state, action) => {
      const { tenant_id, conversation_id } = action.payload;
      if (state.chatsByTenants[tenant_id][conversation_id]) {
        delete state.chatsByTenants[tenant_id][conversation_id];
      }
    },
    handlerReceiveMessage: (state, action) => {
      const message = action.payload;
      if (!message?.id) return;

      const { tenant_id, conversation_id, clientMessageId, type } = message;

      const tenantChats = state.chatsByTenants?.[tenant_id];
      const chat = tenantChats?.[conversation_id];

      if (chat && type === "updateReaction") {
        chat.messages?.forEach((m) => {
          if (m.id === message?.id) m.reactions = message?.reactions || [];
        });
        return;
      }

      if (!chat) {
        state.newMessageCount++;
        return;
      }

      if (!clientMessageId)
        showMessageNotification(message, () => {
          window.location.pathname = ROUTES.CHATS;
        });

      chat.last_message = {
        sent_at: message?.sent_at,
        content: message?.content,
        sender_type: message?.sender_type,
        sender_id: message?.sender_id,
      };

      // 🔁 Optimistic update replace (client → server message)
      if (clientMessageId) {
        const index = chat.messages?.findIndex(
          (msg) => msg.id === clientMessageId,
        );

        delete message?.clientMessageId;

        if (index !== -1) chat.messages[index] = message;
        return;
      }

      // ➕ New incoming message
      chat.messages ??= [];
      chat.messages.push(message);
      state.newMessageCount++;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatsByTenant.pending, (state) => {
        state.loading = true;
      })
      .addCase(getChatsByTenant.fulfilled, upsertChatsByTenant)
      .addCase(getChatsById.fulfilled, upsertChatsByTenant)
      .addCase(createConversation.fulfilled, upsertChatsByTenant)
      .addCase(fetchMessagesByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].loading = true;
      })
      .addCase(fetchMessagesByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id, limit } = action.meta.arg;

        const chat = state.chatsByTenants[tenant_id][conversation_id];

        chat.loading = false;
        chat.messages = [...(data || [])?.reverse(), ...(chat.messages || [])];

        if (data.length < limit) {
          chat.fetchedAll = true;
        }
      })
      .addCase(updateConversation.fulfilled, (state, action) => {
        const { tenant_id, conversation_id, status, name, remove } =
          action.meta.arg?.payload || {};
        const addedUsers = action.meta.arg?.addedUsers;

        const chat = state.chatsByTenants[tenant_id][conversation_id];
        if (status) chat.status = status;
        if (name) chat.name = name;
        if (addedUsers) chat.members = [...chat?.members, ...addedUsers];
        if (remove)
          chat.members = chat?.members?.filter(
            ({ id }) => !remove?.includes(id),
          );
      })
      .addCase(addEditNote.fulfilled, (state, action) => {
        const { tenant_id, conversation_id, conversation_note_id } =
          action.meta.arg;
        const { data, message: successMessage } = action.payload;

        const chat = state.chatsByTenants?.[tenant_id]?.[conversation_id];

        if (!chat) return;

        if (conversation_note_id) {
          const index = chat.notes?.findIndex(
            (note) => note?.id === conversation_note_id,
          );
          if (index !== -1) {
            chat.notes[index] = { ...chat.notes[index], ...data };
          }
        } else {
          chat.notes = [...(chat.notes || []), data];
        }

        message.success(successMessage);
      })
      .addCase(fetchNotesByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].notesLoading = true;
      })
      .addCase(fetchNotesByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id } = action.meta.arg;
        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.notesLoading = false;
        chat.notes = [...(chat.notes || []), ...data];
      })
      .addCase(fetchDocsByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].docsLoading = true;
      })
      .addCase(fetchDocsByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id } = action.meta.arg;
        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.docsLoading = false;
        chat.hasMore = data?.length >= 20;
        chat.docsPage = (chat?.docsPage || 1) + 1;
        chat.docs = [...(chat.docs || []), ...data];
      })
      .addCase(addReminder.fulfilled, (state, action) => {
        const { tenant_id, conversation_id } = action.meta.arg;
        const { data, message: successMessage } = action.payload;

        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.reminders = [...(chat.reminders || []), data];
        message.success(successMessage || "Reminder Added Successful");
      })
      .addCase(fetchRemindersByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].remindersLoading =
          true;
      })
      .addCase(fetchRemindersByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id } = action.meta.arg;
        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.remindersLoading = false;
        chat.reminders = [...(chat.reminders || []), ...data];
      })
      .addCase(
        addUpdateCheckpoint.fulfilled,
        handleAddUpdateCheckpointFulfilled,
      )
      .addCase(fetchChecklistByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].checkListLoading =
          true;
      })
      .addCase(fetchChecklistByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id } = action.meta.arg;
        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.checkListLoading = false;
        chat.checklists = [...(chat.checklists || []), ...data];
      })
      .addCase(addEditCase.fulfilled, (state, action) => {
        const { tenant_id, conversation_id, id } = action.meta.arg;
        const { data, message: successMessage } = action.payload;

        const chat = state.chatsByTenants?.[tenant_id]?.[conversation_id];

        if (!chat) return;

        if (id) {
          const index = chat.cases?.findIndex((c) => c?.id === id);
          if (index !== -1) {
            chat.cases[index] = { ...chat.cases[index], ...data?.[0] };
          }
        } else {
          chat.cases = [data?.[0], ...(chat.cases || [])];
        }

        message.success(successMessage);
      })
      .addCase(fetchCasesByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].casesLoading = true;
      })
      .addCase(fetchCasesByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id } = action.meta.arg;
        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.casesLoading = false;
        chat.cases = [...(chat.cases || []), ...data];
      })
      .addCase(addEditSummary.fulfilled, (state, action) => {
        const { tenant_id, conversation_id } = action.meta.arg;
        const { data, message: successMessage } = action.payload;
        console.log("🚀 ~ slice.js:253 ~ data:", data);

        // const chat = state.chatsByTenants?.[tenant_id]?.[conversation_id];

        // if (!chat) return;

        // chat.summaries = [data, ...(chat.summaries || [])];

        message.success(successMessage);
      })
      .addCase(fetchSummariesByConversationId.pending, (state, action) => {
        const { conversation_id, tenant_id } = action.meta.arg;
        state.chatsByTenants[tenant_id][conversation_id].summaryLoading = true;
      })
      .addCase(fetchSummariesByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { tenant_id, conversation_id, isRefresh } = action.meta.arg;
        const chat = state.chatsByTenants[tenant_id][conversation_id];
        chat.summaryLoading = false;
        if (isRefresh) {
          chat.summaries = [...data];
        } else {
          chat.summaries = [...(chat.summaries || []), ...data];
        }
      })
      .addCase(updateFileKey.fulfilled, (state, action) => {
        const { url } = action.payload;
        const { tenant_id, message_id, conversation_id, doc_id } =
          action.meta.arg;
        const chat = state.chatsByTenants[tenant_id]?.[conversation_id];

        if (!chat) return;

        if (doc_id && chat?.docs?.length) {
          const doc = chat.docs.find((d) => d.id === doc_id);
          if (doc) doc.storage_url = url;
          return;
        }

        const index = chat?.messages?.findIndex(({ id }) => id === message_id);
        if (index !== -1) {
          chat.messages[index].message_attachment.storage_url = url;
        }
      });
  },
});

export const {
  connectWebSocket,
  disconnectWebSocket,
  sendWebSocketMessage,
  setKeyValue,
  handlerReceiveMessage,
  removeChat,
} = chatsSlice.actions;

export default chatsSlice.reducer;
